import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Send } from "lucide-react";
import { LegalPage } from "./LegalPage";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fieldClass = "h-12 rounded-full border-ink/15 bg-white/60 px-5 text-base text-ink placeholder:text-ink/40 focus-visible:ring-2 focus-visible:ring-gold";

const ContactSuccess = ({ email }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="flex items-center gap-3 rounded-full border border-gold/40 px-6 py-4 text-ink"
    data-testid="contact-success-message"
  >
    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold text-ink">
      <Check className="h-3.5 w-3.5" />
    </span>
    <p className="text-sm font-semibold tracking-wide">Message received. We'll get back to you at {email} soon.</p>
  </motion.div>
);

const ContactForm = ({ onSuccess }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    setError("");
    try {
      await axios.post(`${API}/contact`, { name, email, message });
      onSuccess(email);
    } catch (err) {
      setState("error");
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    }
  };

  return (
    <form onSubmit={submit} className="max-w-xl space-y-4" data-testid="contact-form">
      <p className="text-base leading-relaxed text-ink/70">
        Questions about the waitlist, Gold Status, your city, or anything else — send us a note and we'll reply by email.
      </p>
      <Input type="text" required maxLength={100} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" aria-label="Your name" data-testid="contact-name-input" className={fieldClass} />
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" aria-label="Your email address" data-testid="contact-email-input" className={fieldClass} />
      <textarea
        required
        maxLength={2000}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="What's on your mind?"
        aria-label="Your message"
        rows={6}
        data-testid="contact-message-input"
        className="w-full rounded-3xl border border-ink/15 bg-white/60 px-5 py-4 text-base text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-gold"
      />
      {state === "error" && <p className="text-sm font-medium text-red-700" data-testid="contact-error-message">{error}</p>}
      <Button type="submit" disabled={state === "loading"} data-testid="contact-submit-button" className="h-12 rounded-full bg-gold px-8 text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:bg-gold-deep active:scale-95">
        {state === "loading" ? "Sending…" : "Send message"}
        <Send className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
};

const ContactPage = () => {
  const [sentTo, setSentTo] = useState("");
  return (
    <LegalPage title="Contact us" updated="We read every message" testId="contact-page">
      {sentTo ? <ContactSuccess email={sentTo} /> : <ContactForm onSuccess={setSentTo} />}
    </LegalPage>
  );
};

export default ContactPage;
